import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Menu, X, Calculator, Zap, ShoppingCart } from 'lucide-react';
import { trackEvent, EVENTS } from '@/lib/analytics';
import { useCart } from '@/lib/cartContext';

const NAV_LINKS = [
  { to: '/products', label: 'Products' },
  { to: '/solutions', label: 'Solutions' },
  { to: '/calculator', label: 'Calculator' },
  { to: '/case-studies', label: 'Case Studies' },
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Contact' },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { itemCount } = useCart();

  return (
    <nav className="nav-glass sticky top-0 z-50 w-full border-b border-stone-200/50" data-testid="main-navbar">
      <div className="voltide-container flex items-center justify-between h-16 lg:h-18">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="navbar-logo">
          <img src="/images/voltide-logo.svg" alt="Voltide" className="h-8 lg:h-9" />
        </Link>

        <div className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map(link => (
            <Link
              key={link.to}
              to={link.to}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                location.pathname === link.to
                  ? 'text-[#0061DD] bg-blue-50/80'
                  : 'text-stone-600 hover:text-[#0061DD] hover:bg-stone-50'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <Link to="/cart" className="relative p-2 text-stone-600 hover:text-[#0061DD] transition-colors" data-testid="nav-cart-btn">
            <ShoppingCart className="w-5 h-5" />
            {itemCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-[#0061DD] text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="cart-badge">{itemCount}</span>
            )}
          </Link>
          <Link to="/calculator" onClick={() => trackEvent(EVENTS.OPEN_CALCULATOR)}>
            <Button variant="outline" className="rounded-full border-[#0061DD] text-[#0061DD] hover:bg-blue-50 gap-2" data-testid="nav-calc-savings-btn">
              <Calculator className="w-4 h-4" />
              Calculate Savings
            </Button>
          </Link>
          <Link to="/quote" onClick={() => trackEvent(EVENTS.START_RFQ, { source: 'navbar' })}>
            <Button className="rounded-full bg-[#0061DD] hover:bg-[#004BB3] text-white shadow-lg shadow-blue-500/20 gap-2" data-testid="nav-get-quote-btn">
              <Zap className="w-4 h-4" />
              Get a Quote
            </Button>
          </Link>
        </div>

        <button
          className="lg:hidden p-2 text-stone-600 hover:text-[#0061DD]"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-stone-200/50 bg-white/95 backdrop-blur-xl animate-fade-in" data-testid="mobile-menu">
          <div className="voltide-container py-4 space-y-1">
            {NAV_LINKS.map(link => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setOpen(false)}
                className={`block px-4 py-3 text-sm font-medium rounded-lg ${
                  location.pathname === link.to
                    ? 'text-[#0061DD] bg-blue-50'
                    : 'text-stone-600 hover:bg-stone-50'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-3 flex flex-col gap-2">
              <Link to="/cart" onClick={() => setOpen(false)} className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-stone-600 hover:bg-stone-50 rounded-lg">
                <ShoppingCart className="w-4 h-4" /> Quote Cart {itemCount > 0 && <Badge className="rounded-full bg-[#0061DD] text-white text-xs ml-auto">{itemCount}</Badge>}
              </Link>
              <Link to="/calculator" onClick={() => { setOpen(false); trackEvent(EVENTS.OPEN_CALCULATOR); }}>
                <Button variant="outline" className="w-full rounded-full border-[#0061DD] text-[#0061DD]" data-testid="mobile-calc-btn">
                  Calculate Savings
                </Button>
              </Link>
              <Link to="/quote" onClick={() => { setOpen(false); trackEvent(EVENTS.START_RFQ, { source: 'mobile_nav' }); }}>
                <Button className="w-full rounded-full bg-[#0061DD] text-white" data-testid="mobile-quote-btn">
                  Get a Quote
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
